import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function ThermalExpansionCalculator() {
  const [length, setLength] = useState("");
  const [coefficient, setCoefficient] = useState("0.000012");
  const [tempChange, setTempChange] = useState("");
  const [resultLinear, setResultLinear] = useState<number | null>(null);
  const [resultVolume, setResultVolume] = useState<number | null>(null);

  const calculateLinear = () => {
    const L0 = parseFloat(length);
    const alpha = parseFloat(coefficient);
    const deltaT = parseFloat(tempChange);

    if (isNaN(L0) || isNaN(alpha) || isNaN(deltaT)) {
      toast.error("Please enter valid values");
      return;
    }

    const deltaL = L0 * alpha * deltaT;
    const L = L0 + deltaL;
    setResultLinear(L);
    toast.success("Calculation complete!");
  };

  const calculateVolume = () => {
    const V0 = parseFloat(length);
    const beta = parseFloat(coefficient);
    const deltaT = parseFloat(tempChange);

    if (isNaN(V0) || isNaN(beta) || isNaN(deltaT)) {
      toast.error("Please enter valid values");
      return;
    }

    const deltaV = V0 * beta * deltaT;
    const V = V0 + deltaV;
    setResultVolume(V);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setLength("");
    setCoefficient("0.000012");
    setTempChange("");
    setResultLinear(null);
    setResultVolume(null);
  };

  return (
    <PageLayout calculatorName="Thermal Expansion Calculator" calculatorPath="/calculator/thermal-expansion">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Thermal Expansion Formulas</CardTitle>
            <CardDescription>
              Linear: ΔL = αL₀ΔT | Volumetric: ΔV = βV₀ΔT
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="linear" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="linear">Linear Expansion</TabsTrigger>
                <TabsTrigger value="volume">Volumetric Expansion</TabsTrigger>
              </TabsList>

              <TabsContent value="linear" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="lengthLinear">Initial Length (m)</Label>
                    <Input
                      id="lengthLinear"
                      type="number"
                      step="0.01"
                      placeholder="Enter initial length"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coefficientLinear">Linear Expansion Coefficient (1/°C)</Label>
                    <Input
                      id="coefficientLinear"
                      type="number"
                      step="0.000001"
                      placeholder="Steel: 0.000012"
                      value={coefficient}
                      onChange={(e) => setCoefficient(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Steel: 12×10⁻⁶, Aluminum: 23×10⁻⁶, Copper: 17×10⁻⁶</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tempChangeLinear">Temperature Change (°C)</Label>
                    <Input
                      id="tempChangeLinear"
                      type="number"
                      step="0.01"
                      placeholder="Enter temperature change"
                      value={tempChange}
                      onChange={(e) => setTempChange(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateLinear} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultLinear !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {resultLinear.toFixed(6)} m
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Final Length (Change: {(resultLinear - parseFloat(length)).toFixed(6)} m)
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["thermal-expansion"]} trigger={resultLinear} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="volume" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="volumeInitial">Initial Volume (m³)</Label>
                    <Input
                      id="volumeInitial"
                      type="number"
                      step="0.01"
                      placeholder="Enter initial volume"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coefficientVolume">Volume Expansion Coefficient (1/°C)</Label>
                    <Input
                      id="coefficientVolume"
                      type="number"
                      step="0.000001"
                      placeholder="Steel: 0.000036"
                      value={coefficient}
                      onChange={(e) => setCoefficient(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Steel: 36×10⁻⁶, Aluminum: 69×10⁻⁶ (β ≈ 3α)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tempChangeVolume">Temperature Change (°C)</Label>
                    <Input
                      id="tempChangeVolume"
                      type="number"
                      step="0.01"
                      placeholder="Enter temperature change"
                      value={tempChange}
                      onChange={(e) => setTempChange(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateVolume} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultVolume !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {resultVolume.toFixed(6)} m³
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Final Volume (Change: {(resultVolume - parseFloat(length)).toFixed(6)} m³)
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["thermal-expansion"]} trigger={resultVolume} />
                  </>
                )}
              </TabsContent>
            </Tabs>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Choose between linear or volumetric expansion</p>
                <p>2. Enter initial dimension (length or volume)</p>
                <p>3. Enter expansion coefficient for the material</p>
                <p>4. Enter temperature change</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 1 m steel rod heated by 100°C expands to 1.0012 m
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
